/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { NetworkStatus, gql, useQuery } from '@apollo/client';
import { customColors } from '@apollo/orbit';
import { css, jsx } from '@emotion/react';
import { useEffect } from 'react';

import { InsightsL3Nav } from 'src/app/graph/operationsPage/insightsL3Nav/InsightsL3Nav';
import { Loading } from 'src/components/common/loading/Loading';
import { ResizerStyles } from 'src/components/common/resizerStyles/ResizerStyles';
import { SplitPane } from 'src/components/common/splitPane/SplitPane';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { useInsightsL3NavState } from '../../operationsPage/insightsL3Nav/useInsightsL3NavState';
import { insightsRouteConfig } from '../../routes';

import { SubgraphInsightsMainContent } from './SubgraphInsightsMainContent';

export const subgraphInsightsPageQuery = gql<
  GraphQLTypes.SubgraphInsightsPageQuery,
  GraphQLTypes.SubgraphInsightsPageQueryVariables
>`
  query SubgraphInsightsPageQuery($graphId: ID!, $graphVariant: String!) {
    service(id: $graphId) {
      id
      variant(name: $graphVariant) {
        id
        requestsInLastDay
        hasManagedSubgraphs
        hasSupergraphSchema
      }
      account {
        id
      }
    }
  }
`;

export const SubgraphInsightsPage = () => {
  const { isOpen, width, setWidth } = useInsightsL3NavState();
  const params = useRouteParams(insightsRouteConfig);
  const { graphId, graphVariant = Config.service.defaultVariant } = params;

  const { data, loading, networkStatus } = useQuery(subgraphInsightsPageQuery, {
    notifyOnNetworkStatusChange: true,
    variables: {
      graphId,
      graphVariant,
    },
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
  });
  const reportingEnabled = (data?.service?.variant?.requestsInLastDay || 0) > 0;
  const isSupergraphVariant =
    Boolean(data?.service?.variant?.hasManagedSubgraphs) ||
    Boolean(data?.service?.variant?.hasSupergraphSchema);

  const trackCustomEvent = useTrackCustomEvent();
  useEffect(() => {
    trackCustomEvent({
      action: 'landed_on_slp',
      category: 'Subgraph Insights',
    });
  }, [trackCustomEvent]);

  if (loading && networkStatus === NetworkStatus.loading) {
    return <Loading />;
  }

  return !isOpen ? (
    <SubgraphInsightsMainContent
      serviceId={data?.service?.id}
      accountId={data?.service?.account?.id}
      reportingEnabled={reportingEnabled}
      isSupergraphVariant={isSupergraphVariant}
    />
  ) : (
    <div className="relative flex flex-1 flex-col">
      <ResizerStyles />
      <SplitPane
        defaultSize={width}
        css={css`
          & > .Resizer.vertical {
            padding-left: 10px;
            padding-right: 10px;
            border-right: 1px solid ${customColors.border.primary};
            background-color: ${customColors.bg.primary};
          }
          & > .Pane2 {
            overflow-y: auto;
            background-color: ${customColors.bg.primary};
          }
          & > .Pane1 {
            background-color: ${customColors.bg.primary};
          }
        `}
        onDragFinished={setWidth}
      >
        <InsightsL3Nav />
        <SubgraphInsightsMainContent
          serviceId={data?.service?.id}
          accountId={data?.service?.account?.id}
          reportingEnabled={reportingEnabled}
          isSupergraphVariant={isSupergraphVariant}
        />
      </SplitPane>
    </div>
  );
};
